import { createStore } from 'vuex';
import { setLoggingOut } from '../services/authService';
import sidebar from './modules/sidebar';

// Initial state
const initialState = () => ({
	user: {
		email: null,
		userName: null,
		accessToken: null,
		role: null,
	},
	ui: {
		showSubscriptionModal: false,
		showSupportModal: false,
		triggerSupportModal: 0,
	}
});

export default createStore({
	// Use a function to return initial state to allow resetting
	state: initialState(),
	getters: {
		user: state => state.user
	},
	mutations: {
		setUser(state, user) {
			state.user = { ...user }; // Create a new object to ensure reactivity
		},
		setShowSubscriptionModal(state, value) {
			state.ui.showSubscriptionModal = value;
		},
		setShowSupportModal(state, value) {
			state.ui.showSupportModal = value;
		},
		triggerSupportModal(state) {
			state.ui.triggerSupportModal += 1; // Increment to trigger watchers
		},
		clearUser(state) {
			console.log('Clearing user data', state.user);
			
			try {
				// Reset the entire state to initial values
				const newState = initialState();
				Object.keys(newState).forEach(key => {
					state[key] = newState[key];
				});

				// Clear any stored tokens in localStorage/sessionStorage
				['accessToken', 'refreshToken', 'user', 'token', 'auth', 'vuex'].forEach(key => {
					localStorage.removeItem(key);
					sessionStorage.removeItem(key);
				});
				
				// Clear all cookies
				document.cookie.split(';').forEach(cookie => {
					const [name] = cookie.split('=');
					const cookieName = name.trim();
					if (cookieName) {
						document.cookie = `${cookieName}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
						document.cookie = `${cookieName}=; Path=/; Domain=${window.location.hostname}; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
						document.cookie = `${cookieName}=; Path=/; Domain=.${window.location.hostname}; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
					}
				});
			} catch (e) {
				console.error('Error clearing storage:', e);
				throw e; // Re-throw to allow handling in the action
			}
		}
	},
	actions: {
		setUser(context, user) {
			context.commit('setUser', user);
		},
		async refreshUser(context) {
			const res = await fetch('/api/user/me')
			if (!res.ok) throw new Error('Not authenticated') // Basic check

			const user = await res.json()
			await context.commit('setUser', user);
		},
		openSubscriptionModal(context) {
			context.commit('setShowSubscriptionModal', true);
		},
		closeSubscriptionModal(context) {
			context.commit('setShowSubscriptionModal', false);
		},
		openSupportModal(context) {
			context.commit('setShowSupportModal', true);
		},
		closeSupportModal(context) {
			context.commit('setShowSupportModal', false);
		},
		clearUser(context) {
			context.commit('clearUser');
		},
		async logout({ commit }) {
			console.log('Logout action triggered');
			
			// Set the logging out flag first to prevent token refresh attempts
			setLoggingOut(true);
			
			// Clear local user state to prevent race conditions
			commit('clearUser');
			
			// Set flag in sessionStorage to prevent infinite redirects from router guards
			sessionStorage.setItem('manual_logout', 'true');
			
			// Wait a small amount to ensure state changes propagate
			await new Promise(resolve => setTimeout(resolve, 50));
			
			// Before trying server logout, check if we're already in a 401 state
			let skipServerLogout = false;
			
			// Check localStorage flag that might have been set by the auth interceptor
			if (sessionStorage.getItem('auth_failed') === 'true') {
				console.log('Auth already failed, skipping server logout call');
				skipServerLogout = true;
			}
			
			if (!skipServerLogout) {
				try {
					// Set a timeout to avoid hanging forever on the logout request
					const logoutPromise = fetch('/api/oauth/logout', {
						method: 'POST',
						credentials: 'include',
						headers: {
							'Content-Type': 'application/json',
							'Accept': 'application/json',
						},
					});
					
					// Add a timeout to the fetch promise
					const timeoutPromise = new Promise((_, reject) => {
						setTimeout(() => reject(new Error('Logout request timeout')), 3000);
					});
					
					// Race the fetch against a timeout
					const response = await Promise.race([logoutPromise, timeoutPromise]);

					// Even if the response is not OK, we still want to redirect to login
					if (!response.ok) {
						console.warn('Logout response not OK:', response.status);
						
						// If we get a 401 during logout, it means we were already logged out
						if (response.status === 401) {
							sessionStorage.setItem('auth_failed', 'true');
						}
					}
				} catch (error) {
					// Log the error but continue with client-side logout process
					console.error('Logout request error:', error);
				}
			}
			
			// Always clear cookies and local storage to ensure complete logout
			// This helps in case server-side logout failed
			document.cookie.split(';').forEach(cookie => {
				const [name] = cookie.trim().split('=');
				document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
			});
			
			// Use a short delay before redirect to ensure all state clearing completes
			setTimeout(() => {
				// Clear session flags after logout is complete
				sessionStorage.removeItem('manual_logout');
				sessionStorage.removeItem('auth_failed');
				
				// Reset the logging out flag after a safe delay
				setTimeout(() => {
					setLoggingOut(false);
					console.log('Logging out flag reset');
				}, 1000);
				
				window.location.href = '/';
			}, 100);
		}
	},
	modules: {
		sidebar,
	}
})
